import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { hasPermission, ModulePermissions, getRolePermissions } from '../types'

export function usePermissions() {
  const { user } = useAuth()
  const [permissions, setPermissions] = useState<ModulePermissions | null>(
    user ? getRolePermissions(user.role) : null,
  )

  // Listen for permission updates
  useEffect(() => {
    const handlePermissionsUpdate = () => {
      if (user) {
        setPermissions(getRolePermissions(user.role))
      }
    }

    window.addEventListener('permissions-updated', handlePermissionsUpdate)

    return () => {
      window.removeEventListener('permissions-updated', handlePermissionsUpdate)
    }
  }, [user])

  // Update permissions when user changes
  useEffect(() => {
    if (user) {
      setPermissions(getRolePermissions(user.role))
    } else {
      setPermissions(null)
    }
  }, [user])

  const canCreate = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return false
    return permissions[module] === 'full'
  }

  const canEdit = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return false
    return permissions[module] === 'full'
  }

  const canDelete = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return false
    return permissions[module] === 'full'
  }

  const canView = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return false
    return hasPermission(user.role, module, 'read')
  }

  const isReadOnly = (module: keyof ModulePermissions) => {
    if (!user || !permissions) return true
    return permissions[module] === 'read' || permissions[module] === 'own'
  }

  return {
    canCreate,
    canEdit,
    canDelete,
    canView,
    isReadOnly,
    permissions,
    user,
  }
}
